<template>
  <div class="h-screen flex w-full bg-img vx-row no-gutter items-center justify-center" id="page-login"
       v-if="initialized">
    <div class="vx-col sm:w-1/2 md:w-1/2 lg:w-3/4 xl:w-3/5 sm:m-0 m-4">

      <maintenance-bar v-if="maintenance" />

      <vx-card>
        <div class="full-page-bg-color" slot="no-body">

          <vs-row vs-type="flex" vs-w="12">

            <vs-col vs-type="flex" vs-justify="center" vs-align="center" vs-sm="12" vs-lg="6">
              <vs-row vs-type="flex" vs-w="12" vs-align="center" vs-justify="center">
                <vs-col vs-type="flex" vs-justify="center" vs-align="center" vs-w="8" vs-lg="6">
                  <img alt="login" class="mx-auto" src="@/assets/images/pages/login/image.svg" style="width:100%;">
                </vs-col>
              </vs-row>
            </vs-col>

            <div class="vx-col flex sm:w-1 md:w-1 lg:w-1/2 d-theme-dark-bg">
              <div class="p-8 login-tabs-container">
                <div v-if="isSunset">
                  <sunset-login-notice
                    :organization-name="sunsetOrganizationName"
                    :effective-date="sunsetEffectiveDate"
                    :contact-email="sunsetContactEmail"
                  />
                  <vs-button class="float-right mb-5 mt-5" @click="isSunset=false; showVerificationCode=false">
                    Go to sign in page
                  </vs-button>
                </div>
                <div v-if="!isSunset" class="vx-card__title mb-4">
                  <h4 class="mb-4">Login</h4>
                  <p v-show="!showVerificationCode">Welcome back, please login to your account.</p>
                  <p v-show="showVerificationCode">Please enter the two-factor authentication code.</p>
                </div>

                <div v-if="!isSunset">
                  <form autocomplete="on">
                    <template v-if="!showVerificationCode">
                      <vs-input class="w-full" icon="icon icon-user" icon-no-border icon-pack="feather" label="Username"
                        ref="username" name="username" v-validate="'required'" v-model="username"
                        :danger="errors.has('username')" :danger-text="errors.first('username')" autocomplete="on" />

                      <vs-input class="w-full mt-6" icon="icon icon-lock" icon-no-border icon-pack="feather"
                        label="Password" ref="password" name="password" type="password" v-validate="'required'"
                        v-model="password" :danger="errors.has('password')" :danger-text="errors.first('password')"
                        autocomplete="on" />
                    </template>
                    <template v-else>
                      <sm-input-verification-code id="one-time-code" name="one-time-code" autocomplete="one-time-code"
                        class="my-6" v-model="verificationCode" :length="6" :font-size="35"
                        :input-color="errorMessage ? 'rgb(var(--vs-danger))' : 'rgb(var(--vs-primary))'"
                        :font-color="errorMessage ? 'rgb(var(--vs-danger))' : 'rgb(var(--vs-primary))'"
                        :allow-paste="true" :only-number="true" v-validate="{ required: true, min: 6, max: 6 }"
                        data-vv-name="Verification Code" :danger="errors.has('one-time-code')"
                        :danger-text="errors.first('one-time-code')">
                      </sm-input-verification-code>
                    </template>
                  </form>

                  <vs-alert color="danger" :active="errorMessage && !isSunset" class="mt-5 mb-5">
                    {{ errorMessage }}
                  </vs-alert>

                  <template v-if="!showVerificationCode && !isSunset">
                    <div class="flex flex-wrap justify-end my-5">
                      <router-link :to="{ name: 'page-forgotten-password' }">Forgot Password?</router-link>
                    </div>
                    <div class="flex flex-wrap justify-end my-5">
                      <vs-button class="float-right mb-2" @click="onSubmit" :disabled="errors.any() || !isFormDirty">
                        Login
                      </vs-button>
                    </div>
                  </template>
                  <template v-else-if="!isSunset">
                    <div class="vx-row justify-between mt-8">
                      <div class="vx-col">
                        <vs-button class="ml-1" type="flat" @click="onResetMFA">Cancel</vs-button>
                      </div>
                      <div class="vx-col">
                        <vs-button class="mr-1" @click="onSubmitMFA" :disabled="errors.any()">Login</vs-button>
                      </div>
                    </div>
                  </template>
                  <div class="flex flex-wrap justify-end">
                    <span> Have questions? Feel free to <a
                        href="mailto:customers@starlingminds.com?subject=Starling%20Minds%20SP5%20MF%20feedback%20and%20questions"
                        target="_blank">contact us</a> any time</span>
                  </div>
                </div>
              </div>
            </div>
          </vs-row>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
import {halClientOptions, ResourceImpl} from 'hal-client';
import {Auth} from 'aws-amplify';
import MaintenanceBar from '@/layouts/components/navbar/components/maintenance-bar';
import SunsetLoginNotice from '@/views/pages/SunsetLoginNotice.vue';
import SmInputVerificationCode from '@/components/starling/sm-input-verification-code/sm-input-verification-code';

/* eslint-disable security/detect-object-injection */
export default {
  components: { MaintenanceBar, SmInputVerificationCode, SunsetLoginNotice },
  props: {
    publicRoot: {
      type: ResourceImpl,
      required: true
    }
  },
  data () {
    return {
      initialized: false,
      errorMessage: null,
      username: '',
      password: '',
      checkbox_remember_me: false,
      showVerificationCode: false,
      verificationCode: null,
      cognitoUser: null,
      enforceMFA: false,
      isSunset: false,
      sunsetOrganizationName: null,
      sunsetEffectiveDate: null,
      sunsetContactEmail: null
    };
  },
  computed: {
    isFormDirty () {
      return Object.keys(this.fields).some(key => this.fields[key] && this.fields[key].dirty);
    },
    maintenance () {
      return this.$store.state.maintenance.maintenance;
    }
  },
  mounted () {
    if (this.$store.state.router.cognitoUser) {
      this.cognitoUser = this.$store.state.router.cognitoUser;
      this.username = this.cognitoUser.username;
      this.password = this.$store.state.router.newPassword;

      this.$store.state.router.cognitoUser = null;
      this.$store.state.router.newPassword = null;

      if (this.cognitoUser.challengeName === 'SOFTWARE_TOKEN_MFA') {
        this.showVerificationCode = true;
      } else if (this.cognitoUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
        this.$router.push({
          name: 'page-configure-password',
          params: {cognitoUser: this.cognitoUser}
        });
      } else if (this.cognitoUser.challengeName === 'MFA_SETUP' || this.enforceMFA === true) {
        this.$router.push({
          name: 'page-configure-mfa',
          params: {cognitoUser: this.cognitoUser}
        });
      }
    } else {
      Auth.currentSession().then(() => {
        this.$store.state.router.apiRoot.$request().$getSelf().then(root => {
          this.$store.commit('router/updateRouteProp', {
            property: 'apiRoot',
            value: root
          });
          this.$router.push({name: 'app-dashboard'});
        });
      })
        .catch(() => {
        });
    }
    this.initialized = true;
  },
  methods: {
    onResetMFA () {
      this.showVerificationCode = false;
      this.verificationCode = null;
    },
    onSubmit () {
      this.$vs.loading({});
      this.getOrgMFAConfiguration().then(() => {
        return Auth.signIn(this.username.trim(), this.password);
      }).then((user) => {
        this.cognitoUser = user;
        if (user.challengeName === 'SOFTWARE_TOKEN_MFA') {
          this.showVerificationCode = true;
        } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          this.$router.push({
            name: 'page-configure-password',
            params: {cognitoUser: user}
          });
        } else if (user.challengeName === 'MFA_SETUP' || this.enforceMFA === true) {
          this.$router.push({
            name: 'page-configure-mfa',
            params: {cognitoUser: user}
          });
        } else {
          this.login();
        }
      }).catch(err => {
        this.$log.error(err);
        if (err.code === 'UserNotFoundException') {
          this.localLogin();
        } else {
          this.errorMessage = this.errorMessage || 'Bad credentials';
        }
      }).finally(() => {
        this.$vs.loading.close();
      });
    },
    onSubmitMFA () {
      if (!this.verificationCode || this.verificationCode.length !== 6) {
        this.errorMessage = 'Invalid verification code';
        return;
      }
      this.$vs.loading({});
      this.errorMessage = false;
      Auth.confirmSignIn(this.cognitoUser, this.verificationCode, 'SOFTWARE_TOKEN_MFA').then(() => {
        this.login();
      }).catch(() => {
        this.errorMessage = 'Invalid verification code';
      }).finally(() => {
        this.$vs.loading.close();
      });
    },
    login () {
      const session = Auth.currentSession();
      return session.then(tokens => {
        const key = tokens.getAccessToken().jwtToken;
        this.$store.commit('account/setAccessToken', key);
        this.errorMessage = null;
        this.publicRoot.$request().$post('sign_in_cognito', null, {
          username: this.username.trim(),
          password: this.password
        }).then(() => {
          this.$store.state.router.apiRoot.$request().$getSelf().then(root => {
            this.$store.commit('router/updateRouteProp', {
              property: 'apiRoot',
              value: root
            });
            this.$router.push({ name: 'app-dashboard' });
          });
        }, err => {
          this.$log.error(err);
          const data = (err && err.response && err.response.data) ? err.response.data : (err || {});
          const isSunset = data && data.error === 'ORG_SUNSET';
          if (isSunset) {
            try { halClientOptions.headers.set('Authorization', null); } catch (e) { /* silent swallow */ }
            this.$store.dispatch('account/removeAccessToken').finally(() => {
              this.sunsetOrganizationName = data.organizationName || null;
              this.sunsetEffectiveDate = data.effectiveDate || null;
              this.sunsetContactEmail = data.organizationContactEmail || null;
              this.isSunset = true;
              // refresh apiRoot unauthenticated to drop private links
              if (this.$store.state.router.apiRoot && this.$store.state.router.apiRoot.$request) {
                this.$store.state.router.apiRoot.$request().$getSelf().then(root => {
                  this.$store.commit('router/updateRouteProp', {
                    property: 'apiRoot',
                    value: root
                  });
                }).catch(() => {});
              }
            });
          } else {
            this.errorMessage = 'Bad credentials';
          }
        }).finally(() => {
          this.$vs.loading.close();
        });
      });
    },
    localLogin () {
      this.$vs.loading({});
      this.publicRoot.$request().$post('sign_in', null, {
        username: this.username.trim(),
        password: this.password,
        rememberMe: this.rememberMe
      }).then(() => {
        this.onSubmit();
      }, err => {
        this.$log.error(err);
        const data = (err && err.response && err.response.data) ? err.response.data : (err || {});
        const isSunset = data && data.error === 'ORG_SUNSET';
        if (isSunset) {
          try { halClientOptions.headers.set('Authorization', null); } catch (e) { /* silent swallow */ }
          this.$store.dispatch('account/removeAccessToken').finally(() => {
            this.sunsetOrganizationName = data.organizationName || null;
            this.sunsetEffectiveDate = data.effectiveDate || null;
            this.sunsetContactEmail = data.organizationContactEmail || null;
            this.isSunset = true;
            // refresh apiRoot unauthenticated to drop private links
            if (this.$store.state.router.apiRoot && this.$store.state.router.apiRoot.$request) {
              this.$store.state.router.apiRoot.$request().$getSelf().then(root => {
                this.$store.commit('router/updateRouteProp', {
                  property: 'apiRoot',
                  value: root
                });
              }).catch(() => {});
            }
          });
        } else {
          this.errorMessage = err.detail || 'Bad credentials';
        }
      }).finally(() => {
        this.$vs.loading.close();
      });
    },
    getOrgMFAConfiguration () {
      return this.publicRoot.$request().$get('mfa_enforcement', { hqUser: this.username.trim() }).then(res => {
        this.enforceMFA = res.MfaEnforcement;
        return Promise.resolve(res.MfaEnforcement);
      }, err => {
        this.$log.error(err);
        this.errorMessage = 'Internal error. Please check your credentials and try again.';
        throw err;
      });
    }
  }
};
</script>
